import { GetAllContracts, IContractResponse } from "@services/contracts";
import {
  GetAdminSettingsRequest,
  GetUserSettingsRequest,
} from "@services/getSettings";
import { GetUsersRequestNoParams, UserI } from "@services/getUsers";
import { AccountRequest } from "@services/http/auth";
import { LoginData } from "@services/http/auth/dto";
import {
  RecordResponse,
  RecordsResponse,
} from "@services/http/responses/BaseResponse";
import useApiRequests from "@services/http/useApiRequest";
import {
  GetMinerProfiles,
  IMinerProfilesResponse,
} from "@services/minerProfiles";
import { RootDispatch, RootState } from "@store";
import AUTHSTATES from "@store/auth/AuthStates";
import { logout, updateAuth } from "@store/auth/actions";
import { isTokenAlive } from "@store/auth/cookieStorage";
import { setContracts } from "@store/contracts/action";
import { IStoredSetting } from "@store/me/actionTypes";
import {
  setMeLoaders,
  setPoolSettings,
  setTgAccounts,
  updateMe,
} from "@store/me/actions";
import { setMinerProfiles } from "@store/minerProfiles/actions";
import { getAllTypes } from "@store/minerTypes/actions";
import { setMiningPlans } from "@store/miningPlans/actions";
import { setUsers } from "@store/users/actions";
import { dev } from "@utils/dev";
import { getLangFromLocal } from "@utils/lang";
import { loadCache } from "@utils/localCache";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "./useAuth";

export const allMinerProfilesCacheKey = "24gi24o98asd1asd1dasderouqeig";
type AllMinerProfilesUpdater = () => Promise<void>;

export const useAllMinerProfiles = (): AllMinerProfilesUpdater => {
  const { enqueue } = useApiRequests();
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);

  const r = useCallback(async () => {
    if (!auth.isAdmin) return;
    const res = await enqueue<IMinerProfilesResponse>(new GetMinerProfiles());
    if (!res) return;
    dispatch(
      setMinerProfiles({
        profiles: res.Records.sort((a, b) => a.name.localeCompare(b.name)),
        Total: res.Total,
      })
    );
  }, [dispatch]);

  return r;
};

export const allContractsCacheKey = "24gi24o985fnvqjieoirgqasdadasderouqeig";
type AllContractUpdater = () => Promise<void>;

export function useContractsRepo() {

  const requests = useApiRequests();
  const dispatch = useDispatch();

  const load = async () => {
    let contracts = loadCache<IContractResponse["Records"]>(allUsersCacheKey);
    if (!contracts) {
      const response = await requests.enqueue<IContractResponse>(
        new GetAllContracts()
      );
      if (!response) return;
      contracts = response.Records;
    }
    dispatch(setContracts(contracts));
  }

  return {
    load
  }
}

export function useAllContractsUpdater(cb?: any): AllContractUpdater {
  // dev.log("call useAllUsersUpdater");

  const requests = useApiRequests();
  const dispatch = useDispatch();
  const auth = useAuth();
  // const me = useMe();
  const initialized = useSelector(
    (state: RootState) => state.users.initialized
  );

  const r = useCallback(async () => {
    if (initialized || !auth.isAdmin) return;
    let ctrList = loadCache<IContractResponse["Records"]>(allUsersCacheKey);
    if (!ctrList) {
      const contracts = await requests.enqueue<IContractResponse>(
        new GetAllContracts(),
        {
          onLoadingStatusChanged: cb,
        }
      );
      if (!contracts) return;
      ctrList = contracts.Records;
      // saveCache(allUsersCacheKey, usrList, 10 * MINUTE); //save for 10 minutes
    } else {
      //dev.log("useAllUsersUpdater, load from cache");
    }
    dispatch(setContracts(ctrList));
  }, [requests, dispatch, auth.isAdmin, initialized]);

  return r;
}

type AllUsersUpdater = () => Promise<void>;

export const allUsersCacheKey = "24gi24o985fnvqjieoirgqerouqeig";

export function useAllUsersUpdater(cb?: any): AllUsersUpdater {
  // dev.log("call useAllUsersUpdater");
  const requests = useApiRequests();
  const dispatch = useDispatch();
  const auth = useAuth();
  // const me = useMe();
  const initialized = useSelector(
    (state: RootState) => state.users.initialized
  );

  const r = useCallback(async () => {
    if (initialized || !auth.isAdmin) return;
    let usrList = loadCache<UserI[]>(allUsersCacheKey);
    if (!usrList) {
      const users = await requests.enqueue<RecordsResponse<UserI>>(
        new GetUsersRequestNoParams(),
        {
          onLoadingStatusChanged: cb,
        }
      );
      if (!users) return;
      usrList = users.Records;
      // saveCache(allUsersCacheKey, usrList, 10 * MINUTE); //save for 10 minutes
    }
    //console.log("useAllUsersUpdater", usrList);
    dispatch(setUsers(usrList));
  }, [requests, dispatch, auth.isAdmin, initialized]);

  return r;
}

type SettingsUpdater = () => Promise<void>;
export const settingsCacheKey = "rwiegrw3wori6qproim1vkanfqwoierqeuqi";

export function useSettingsUpdater(): SettingsUpdater {
  const requests = useApiRequests();
  const dispatch = useDispatch();
  const auth = useAuth();
  // const me = useMe();
  const initialized = useSelector(
    (state: RootState) => state.me.settingsInitialized
  );

  const onLoadingSettings = (load: boolean) => {
    dispatch(setMeLoaders({ settings: load }));
  };

  const r = useCallback(async () => {
    if (initialized || auth.state !== AUTHSTATES.AUTHORIZED) return;
    let settList = loadCache<IStoredSetting[]>(settingsCacheKey) || [];
    if (!settList.length) {
      const request: GetAdminSettingsRequest | GetUserSettingsRequest =
        auth.isAdmin
          ? new GetAdminSettingsRequest()
          : new GetUserSettingsRequest();

      const settings = await requests.enqueue<RecordResponse<IStoredSetting[]>>(
        request,
        {
          onLoadingStatusChanged: onLoadingSettings,
        }
      );

      if (!settings) return;
      const s: IStoredSetting[] = [];
      settings.Record.forEach((setting) => {
        s.push({
          ...setting,
          id: setting.id || 0,
        });
      });
      settList = s;
      // saveCache(settingsCacheKey, settList, 10 * MINUTE); //save for 10 minutes
    }
    dispatch(setPoolSettings(settList));
  }, [requests, dispatch, auth.isAdmin, initialized]);

  return r;
}

export default function useCurrentUser() {
  const dispatch = useDispatch() as RootDispatch;
  const requests = useApiRequests();

  const locale = getLangFromLocal();

  const DEFAULT_PLAN = {
    id: 3,
    name: locale === "RU" ? "Без плана" : "Default",
    meta: "",
    schedule: [],
  };

  useEffect(() => {
    if (!isTokenAlive()) {
      return;
    }
    (async () => {
      if (isTokenAlive()) {
        const response = await requests.enqueueSilent<
          RecordResponse<LoginData>
        >(new AccountRequest({}));
        if (!response || !response.Record || !response.Record.account) {
          dispatch(logout("useCurrentUser"));
          return;
        }
        const { account, types, plans } = response.Record;

        const localizedPlans = plans?.filter((p) => p.id !== 3);

        dispatch(
          updateAuth({
            id: account.Id,
            state: isTokenAlive()
              ? AUTHSTATES.AUTHORIZED
              : AUTHSTATES.UNAUTHORIZED,
            isAdmin: account.is_admin || false,
            email: {
              isPrivate: account.email.is_private,
              value: account.email.value,
            },
            role: account.role,
            uuid: account.uuid,
            name: account.name ?? "",
            controllerId: account.controller_id,
            m_online: account.m_online,
            m_low: account.m_low,
            m_offline: account.m_offline,
          })
        );
        dispatch(updateMe(account));
        dispatch(getAllTypes(types || []));
        dispatch(setMiningPlans([DEFAULT_PLAN, ...(localizedPlans || [])]));
        /// пока отключаем
        dispatch(setTgAccounts([]));
        /*
        const tgAccs = await requests.enqueueSilent<
          RecordResponse<TgAccountsI[]>
        >(new GetTgAccountsRequest());
        if (tgAccs && tgAccs.Record) {
          dispatch(setTgAccounts(tgAccs.Record));
        } else {
          dispatch(setTgAccounts([]));
        }*/
      }
    })();
  }, []);
}
